import React, { useContext, useRef, useState, useMemo } from 'react';
import { useAsync } from 'react-commons';
import { Row, Column, TableInstance } from 'react-table';
import { Button } from 'react-bootstrap';
import { Link, RouteComponentProps } from 'react-router-dom';
import _ from 'lodash';
import ConfigContext from '../../../contexts/ConfigContext';
import ErrorBlock from '../../common/ErrorBlock';
import Spinner from '../../common/Spinner';
import DataTable from '../../common/datatables/DataTable';
import { generateCSV } from '../../common/listUtils';
import {
    DEFAULT_ADMIN_INITIAL_STATE,
    AmStatusType,
    EXPORT_DATE_FORMAT,
} from '../../../utils/Constants';
import useStateWithSessionStorage from '../../../hooks/useStateWithSessionStorage';
import {
    formatAmDisplayString,
    renderDateCell,
    formatCurrency,
    formatDate,
    formatExportText,
} from '../../../utils/displayUtils';
import { getStatusChangeRequests } from '../../../api/statusChangeRequestApi';
import { StatusChangeRequestItem } from '../../../api/statusChangeRequestTypes';
import AmProcessModal, { AmProcessFormType } from '../../detail/modal/AmProcessModal';
import { generateStatusChangeRequestSubRow } from './StatusChangeRequestSubRow';
import BulkStatusChangeModal from './modal/BulkStatusChangeModal';

const StatusChangeRequest = (routerProps: RouteComponentProps) => {
    const config = useContext(ConfigContext);

    const [selecting, setSelecting] = useState<boolean>(false);
    const [selectedRecords, setSelectedRecords] = useState<Map<number, StatusChangeRequestItem>>(
        new Map(),
    );
    const [initialState, setInitialState] = useStateWithSessionStorage(
        'StatusChangeRequestInitialState',
        JSON.stringify(DEFAULT_ADMIN_INITIAL_STATE),
    );

    const defaultState: Array<StatusChangeRequestItem> = [];
    const { value, error, loading, refresh } = useAsync<Array<StatusChangeRequestItem>>(
        () => getStatusChangeRequests(config),
        defaultState,
    );

    const tableInstanceRef = useRef<TableInstance<StatusChangeRequestItem> | null>(null);

    if (error) {
        return typeof error === 'object' ? (
            <ErrorBlock errorMessage="Network Error!" />
        ) : (
            <ErrorBlock errorMessage={error} />
        );
    }

    const isCloseable = (statusChangeRequest: StatusChangeRequestItem) =>
        statusChangeRequest.amStatus === AmStatusType.Approved ||
        (statusChangeRequest.amStatus === AmStatusType.Inactive &&
            (statusChangeRequest.priorStatus === AmStatusType.Draft ||
                statusChangeRequest.priorStatus === AmStatusType.Pending ||
                statusChangeRequest.priorStatus === AmStatusType.Hold ||
                statusChangeRequest.priorStatus === AmStatusType.Approved));

    const statusChangeRequestOptions = (statusChangeRequest: StatusChangeRequestItem) => (
        <div className="btn-group options-icon">
            <button
                type="button"
                className="btn btn-link p-0"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
            >
                <span className="fas fa-ellipsis-h text-muted" />
            </button>

            <ul className="dropdown-menu dropdown-menu-right">
                <li>
                    <button
                        type="button"
                        className="dropdown-menu-modal-toggle"
                        onClick={() => {
                            routerProps.history.push({
                                pathname: `/details/${statusChangeRequest.id}`,
                                state: {
                                    returnPageName: 'Status Change Requests',
                                    returnPathName: '/admin/statusChangeRequest',
                                },
                            });
                        }}
                    >
                        <i className="far fa-info-circle button-icon" /> AM Details
                    </button>
                </li>
                <li role="separator" className="dropdown-divider" />
                {isCloseable(statusChangeRequest) && (
                    <li>
                        <AmProcessModal
                            title="Close Action Memo"
                            toggleButton={
                                <Button
                                    variant="link"
                                    className="dropdown-menu-modal-toggle dropdown-item"
                                >
                                    Close AM
                                </Button>
                            }
                            formType={AmProcessFormType.CLOSE}
                            amId={statusChangeRequest.id}
                            amNumber={statusChangeRequest.amNumber}
                            supNumber={statusChangeRequest.supNumber}
                            onSubmit={() => refresh()}
                        />
                    </li>
                )}
                <li>
                    <AmProcessModal
                        title="Cancel Status Change Request"
                        toggleButton={
                            <Button
                                variant="link"
                                className="dropdown-menu-modal-toggle dropdown-item text-danger"
                            >
                                Cancel Request
                            </Button>
                        }
                        formType={AmProcessFormType.CANCEL_REQUEST}
                        amId={statusChangeRequest.id}
                        amNumber={statusChangeRequest.amNumber}
                        supNumber={statusChangeRequest.supNumber}
                        onSubmit={() => refresh()}
                    />
                </li>
            </ul>
        </div>
    );

    const renderRowSubComponent = generateStatusChangeRequestSubRow();

    const columns = [
        {
            id: 'menu',
            accessor: 'menu',
            Header: (table: { page: { original: StatusChangeRequestItem }[] }) =>
                useMemo(
                    () =>
                        selecting && selectedRecords && setSelectedRecords ? (
                            <span>
                                <i className="fas fa-plus-square invisible" />
                                <input
                                    className="ml-3"
                                    type="checkbox"
                                    checked={table.page.every((row) => {
                                        if (!isCloseable(row.original)) {
                                            return true;
                                        }
                                        return selectedRecords.has(row.original.id);
                                    })}
                                    onChange={(e) => {
                                        const selectedRecordsMap = _.cloneDeep(selectedRecords);
                                        table.page.forEach((row) => {
                                            if (e.target.checked) {
                                                if (isCloseable(row.original)) {
                                                    selectedRecordsMap.set(
                                                        row.original.id,
                                                        row.original,
                                                    );
                                                }
                                            } else {
                                                selectedRecordsMap.delete(row.original.id);
                                            }
                                        });
                                        setSelectedRecords(selectedRecordsMap);
                                    }}
                                />
                            </span>
                        ) : null,
                    [],
                ),
            Cell: ({ row }: { row: Row<StatusChangeRequestItem> }) =>
                useMemo(
                    () => (
                        <span {...row.getToggleRowExpandedProps()}>
                            {row.isExpanded ? (
                                <i className="fas fa-minus-square" />
                            ) : (
                                <i className="fas fa-plus-square" />
                            )}
                            {selecting &&
                            selectedRecords &&
                            row.original.id &&
                            isCloseable(row.original) ? (
                                <input
                                    key={`${row.original.id}`}
                                    className="ml-3"
                                    type="checkbox"
                                    checked={selectedRecords.has(row.original.id)}
                                    onChange={(e) => {
                                        if (row.original.id) {
                                            const selectedRecordsMap = _.cloneDeep(selectedRecords);
                                            if (e.target.checked) {
                                                selectedRecordsMap.set(
                                                    row.original.id,
                                                    row.original,
                                                );
                                            } else {
                                                selectedRecordsMap.delete(row.original.id);
                                            }

                                            setSelectedRecords(selectedRecordsMap);
                                        }
                                    }}
                                />
                            ) : (
                                <input type="checkbox" className="invisible ml-3" />
                            )}
                        </span>
                    ),
                    [],
                ),
            disableSortBy: true,
            width: 75,
        },
        {
            Header: 'Requested On',
            accessor: 'requestedOn',
            Cell: renderDateCell,
            width: 150,
        },
        {
            Header: 'AM#',
            accessor: 'amNumber',
            Cell: (cellProps: { value: number; row: { original: StatusChangeRequestItem } }) =>
                useMemo(
                    () => (
                        <Link
                            to={{
                                pathname: `/details/${cellProps.row.original.id}`,
                                state: {
                                    returnPageName: 'Status Change Requests',
                                    returnPathName: '/admin/statusChangeRequest',
                                },
                            }}
                        >
                            {formatAmDisplayString(
                                cellProps.row.original.amNumber,
                                cellProps.row.original.supNumber,
                            )}
                        </Link>
                    ),
                    [],
                ),
            width: 100,
        },
        {
            Header: 'Subject',
            accessor: 'subject',
            width: 300,
        },
        {
            Header: 'Budget',
            accessor: 'budgetAmount',
            Cell: (cellProps: { value: number }) =>
                useMemo(() => <span>{formatCurrency(cellProps.value, 2)}</span>, []),
            width: 200,
        },
        {
            Header: 'Current Status',
            accessor: 'amStatus',
            width: 200,
        },
        {
            Header: 'Requested Status',
            accessor: 'requestedStatus',
            width: 200,
        },
        {
            Header: 'Comment',
            accessor: 'commentMessage',
            width: null,
        },
        {
            id: 'options',
            Header: '',
            className: 'table-cell-pointer overflow-visible',
            Cell: (cellProps: { row: { original: StatusChangeRequestItem } }) =>
                statusChangeRequestOptions(cellProps.row.original),
            disableSortBy: true,
            width: 55,
        },
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="row row-page-header">
                <div className="col-12 d-flex align-items-center mb-2">
                    <h3 className="mr-auto text-weight-normal mb-0">Status Change Requests</h3>

                    {!selecting ? (
                        <Button
                            variant="outline-primary"
                            className="mobile-hide ml-1"
                            disabled={value.length <= 0}
                            onClick={() => {
                                setSelecting(true);
                            }}
                        >
                            <i className="fas fa-hand-paper mr-2" /> Close AMs
                        </Button>
                    ) : (
                        <>
                            <BulkStatusChangeModal
                                title="Close Action Memos"
                                toggleButton={
                                    <Button
                                        variant="primary"
                                        className="mobile-hide ml-1"
                                        disabled={selectedRecords.size <= 0}
                                    >
                                        <i className="fas fa-hand-paper mr-2" /> Bulk Close AMs
                                    </Button>
                                }
                                bulkSelectItems={Array.from(selectedRecords.values()).map(
                                    (bulkSelectItem) => ({
                                        amId: bulkSelectItem.id,
                                        amNumber: bulkSelectItem.amNumber,
                                        supNumber: bulkSelectItem.supNumber,
                                    }),
                                )}
                                onSubmit={() => {
                                    setSelecting(false);
                                    setSelectedRecords(new Map());
                                    refresh();
                                }}
                            />
                            <Button
                                variant="outline-secondary"
                                className="mobile-hide ml-1"
                                onClick={() => {
                                    setSelecting(false);
                                    setSelectedRecords(new Map());
                                }}
                            >
                                Cancel Bulk Select
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outline-primary"
                        className="ml-1"
                        disabled={loading || value.length <= 0}
                        onClick={() => {
                            if (tableInstanceRef.current) {
                                const list = tableInstanceRef.current.rows
                                    .map((row) => row.original)
                                    .map((statusChangeRequest) => ({
                                        'Requested On': formatDate(
                                            statusChangeRequest.requestedOn,
                                            EXPORT_DATE_FORMAT,
                                        ),
                                        'Action Memo #': statusChangeRequest.amNumber,
                                        'Supplement #': statusChangeRequest.supNumber,
                                        Subject: formatExportText(statusChangeRequest.subject),
                                        Budget: `"${formatCurrency(
                                            statusChangeRequest.budgetAmount,
                                        )}"`,
                                        'Current Status': statusChangeRequest.amStatus,
                                        'Requested Status': statusChangeRequest.requestedStatus,
                                        Comment: formatExportText(
                                            statusChangeRequest.commentMessage,
                                        ),
                                    }));
                                generateCSV(list, 'StatusChangeRequests.csv');
                            }
                        }}
                    >
                        <i className="fas fa-file-export" /> Export CSV
                    </Button>
                </div>
            </div>

            <div className="card border-top">
                <div className="card-body">
                    {!loading ? (
                        <DataTable<StatusChangeRequestItem>
                            columns={columns as Column<StatusChangeRequestItem>[]}
                            data={value}
                            initialState={JSON.parse(initialState)}
                            setInitialState={setInitialState}
                            noGlobalFilter
                            renderRowSubComponent={renderRowSubComponent}
                            getInstanceCallback={(tableInstance) => {
                                if (tableInstanceRef) {
                                    tableInstanceRef.current = tableInstance;
                                }
                            }}
                        />
                    ) : (
                        <Spinner />
                    )}
                </div>
            </div>
        </div>
    );
};

export default StatusChangeRequest;
